import cn from 'classnames';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';

import LeftArrowIcon from '@icons/24/arrow-left-new-design.svg';

import Button from '../../Button';
import styles from './Modal.module.scss';
import { ModalProps } from './types';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const DEFAULT_MODAL_WIDTH = 450;
const DEFAULT_DRAWER_WIDTH = 312;

export function Modal({
    isOpen,
    onClose,
    children,
    title,
    showCloseButton = false,
    closeButtonAriaLabel,
    isFooterLine = false,
    disableAutoFocus = false,
    disableFocusLock = false,
    disableRestoreFocus = false,
    closeButtonText,
    confirmButtonText,
    disabledConfirmBtn,
    width: widthProp,
    side,
    closeOnBackdropClick = true,
    withIconBack = false,
    onBack,
    mobileType = 'fullWidth',
    titlePosition: titlePositionProp,
    titleCentered,
    titleCenteredAlways,
    className,
    customCss,
    handlerSubmit,
    onCloseButtonClick,
}: ModalProps) {
    const titlePosition =
        titlePositionProp ?? (titleCenteredAlways ? 'centeredAlways' : titleCentered ? 'centeredMobile' : undefined);
    const hasHeader = Boolean(title) || showCloseButton;
    const [mounted, setMounted] = useState(false);
    const width = widthProp ?? (side ? DEFAULT_DRAWER_WIDTH : DEFAULT_MODAL_WIDTH);
    const isViewportSized = !side && (width === '100vw' || width === '100%');

    const modalRef = useRef<HTMLDivElement>(null);
    const previousFocus = useRef<HTMLElement | null>(null);

    const handleBack = onBack ?? onClose;

    useEffect(() => {
        if (!isOpen) return;

        setMounted(true);

        previousFocus.current = document.activeElement as HTMLElement;

        document.body.style.overflow = 'hidden';

        requestAnimationFrame(() => {
            modalRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
        });
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) return;

        document.body.style.overflow = '';

        const timeout = setTimeout(() => {
            setMounted(false);
            previousFocus.current?.focus();
        }, 300);

        return () => clearTimeout(timeout);
    }, [isOpen]);

    useEffect(() => {
        if (!mounted) return;

        const handleKeyDown = (e: KeyboardEvent | any) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [mounted, onClose]);

    if (!mounted) return null;

    return createPortal(
        <div
            className={cn(
                styles.backdrop,
                isOpen ? styles['backdrop--enter'] : styles['backdrop--exit'],
                !side && mobileType === 'bottom' && styles.backdrop_bottom,
                side === 'left' && styles.backdrop_left,
                side === 'right' && styles.backdrop_right
            )}
            onClick={closeOnBackdropClick ? onClose : undefined}
        >
            <FocusLock
                autoFocus={!disableAutoFocus}
                disabled={disableFocusLock || !isOpen}
                returnFocus={!disableRestoreFocus}
                className={cn(styles.focusLock, (side || isViewportSized) && styles.focusLock_fill)}
            >
                <div
                    ref={modalRef}
                    role="dialog"
                    aria-modal="true"
                    className={cn(
                        styles.modal,
                        !side && styles[`mobile_${mobileType}`],
                        !side && (isOpen ? styles.enter : styles.exit),
                        !side && styles[`modal_${mobileType}`],
                        isViewportSized && styles.modal_viewport,
                        side && styles.modal_side,
                        side && styles[`modal_side_${side}`],
                        side && (isOpen ? styles[`drawer_${side}_enter`] : styles[`drawer_${side}_exit`]),
                        className
                    )}
                    onClick={e => e.stopPropagation()}
                    style={{ width }}
                >
                    {hasHeader && (
                        <div
                            className={cn(
                                styles.modal_header,
                                titlePosition === 'centeredAlways' && styles.modal_header_centeredAlways
                            )}
                        >
                            {title ? (
                                <>
                                    {!titlePosition && (
                                        <div className={styles.modal_title}>
                                            {withIconBack && (
                                                <LeftArrowIcon
                                                    className={styles.modal_title_arrow}
                                                    onClick={handleBack}
                                                />
                                            )}
                                            <div>{title}</div>
                                        </div>
                                    )}
                                    {titlePosition === 'centeredMobile' && (
                                        <>
                                            <LeftArrowIcon className={styles.modal_title_arrow} onClick={handleBack} />
                                            <div className={styles.modal_title}>{title}</div>
                                        </>
                                    )}

                                    {titlePosition === 'centeredAlways' && (
                                        <>
                                            <div className={styles.modal_header_start}>
                                                {withIconBack ? (
                                                    <LeftArrowIcon
                                                        className={styles.modal_title_arrow}
                                                        onClick={handleBack}
                                                    />
                                                ) : null}
                                            </div>
                                            <div className={styles.modal_title_absoluteCenter}>{title}</div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className={styles.modal_title} />
                            )}

                            <button
                                type="button"
                                {...(closeButtonAriaLabel ? { 'aria-label': closeButtonAriaLabel } : {})}
                                className={cn(
                                    styles.modal_onClose,
                                    withIconBack && !(titlePosition !== 'centeredMobile') && styles.modal_onClose_hide
                                )}
                                onClick={onClose}
                            ></button>
                        </div>
                    )}

                    <div className={cn(styles.modal_content, customCss?.contentClassName)}>{children}</div>

                    {(confirmButtonText || closeButtonText || isFooterLine) && (
                        <div className={cn(!side && mobileType === 'fullWidth' && styles.modal_down)}>
                            <div
                                className={cn(styles.modal_footer, isFooterLine && styles.modal_footer_line)}
                                style={customCss?.footerLine}
                            >
                                {closeButtonText && (
                                    <Button
                                        size="lg"
                                        variant="secondaryWhite"
                                        fullWidth={Boolean(customCss?.footerLine)}
                                        onClick={onCloseButtonClick ?? onClose}
                                    >
                                        {closeButtonText}
                                    </Button>
                                )}
                                {confirmButtonText && (
                                    <Button
                                        disabled={disabledConfirmBtn}
                                        size="lg"
                                        type="submit"
                                        fullWidth={Boolean(customCss?.footerLine)}
                                        onClick={handlerSubmit}
                                        style={customCss?.confirmButtonWrapper}
                                    >
                                        {confirmButtonText}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </FocusLock>
        </div>,
        document.body
    );
}
